import InputLabel from "components/inputfields/InputLabel";
import InputTextField from "components/inputfields/InputTextField";
import IProduct from "models/product";
import { IFoodlaCategoryOption } from "models/category";
import { useDebouncedStateString } from "hooks/useDebouncedState";

import { isComplementarySupplements, isCompleteFeed, isFeedMixtures } from "./category";

interface IFeedFieldsDataProps {
  readOnly?: boolean;
  approve: boolean;
  state: IProduct;
  setState: (state: IProduct | ((prevState: IProduct) => IProduct)) => void;
  rootCategory?: IFoodlaCategoryOption;
};

const FeedFields = ({ readOnly, approve, state, setState, rootCategory }: IFeedFieldsDataProps) => {
  // Use debounced state hook for each field
  const [animalFoodIngredients, handleAnimalFoodChange] = useDebouncedStateString(
    state.animalFoodIngredients || '',
    setState,
    'animalFoodIngredients',
    300
  );

  const [feedAdditives, handleFeedAdditivesChange] = useDebouncedStateString(
    state.feedAdditives || '',
    setState,
    'feedAdditives',
    300
  );

  const [feedingInstructions, handleFeedingInstructionsChange] = useDebouncedStateString(
    state.feedingInstructions || '',
    setState,
    'feedingInstructions',
    300
  );

  const [analyticalConstituentsFeed, handleAnalyticalConstituentsChange] = useDebouncedStateString(
    state.analyticalConstituentsFeed || '',
    setState,
    'analyticalConstituentsFeed',
    300
  );

  return (
    <div style={{width: '100%'}}>
      <InputLabel
        heading="Fodersammansättning"
        text="Ange fodersammansättning som deklareras på förpackningen. Läs mer om kraven på sidan 123."
        link="https://gs1.se/wp-content/uploads/sites/2/2023/08/handledning-artikelinformation_3.1.24_1_1.pdf"
      />
      <InputTextField
        readOnly={readOnly}
        state={state}
        setState={setState}
        inputName="animalFoodIngredients"
        value={animalFoodIngredients}
        placeholder="Ex. Laxprotein 20%, Potatis 8%, Havre 5%, Laxolja 1%. Lista separerad med komma"
        approve={approve && !!state?.approve?.find(x => x.field === 'animalFoodIngredients')}
        onChange={(e) => handleAnimalFoodChange(e.target.value)}
        multiline
        multilineRows={2}
        multilineMax={20}
      />
      <InputLabel
        heading="Fodertillsatser"
        text="Lista över tillsatta ämnen som ingår i foderets sammansättning. Ange tillsatser såsom det deklareras på förpackningen. Läs mer om kraven på sidan 123."
        link="https://gs1.se/wp-content/uploads/sites/2/2023/08/handledning-artikelinformation_3.1.24_1_1.pdf"
      />
      <InputTextField
        readOnly={readOnly}
        state={state}
        setState={setState}
        inputName="feedAdditives"
        value={feedAdditives}
        placeholder="Ex. Vitamin A: 12 500 IE/kg. Lista separerad med komma"
        onChange={(e) => handleFeedAdditivesChange(e.target.value)}
        approve={approve && !!state?.approve?.find(x => x.field === 'feedAdditives')}
        multiline
        multilineRows={2}
        multilineMax={20}
      />
      <InputLabel
        heading="Utfodringsinstruktioner"
        text="Beskrivning i fritext av hur djuret ska utfodras. Läs mer om kraven på sidan 123."
        link="https://gs1.se/wp-content/uploads/sites/2/2023/08/handledning-artikelinformation_3.1.24_1_1.pdf"
      />
      <InputTextField
        readOnly={readOnly}
        state={state}
        setState={setState}
        inputName="feedingInstructions"
        value={feedingInstructions}
        placeholder="Instruktioner för matning och dosering, inklusive eventuella försiktighetsåtgärder"
        onChange={(e) => handleFeedingInstructionsChange(e.target.value)}
        approve={approve && !!state?.approve?.find(x => x.field === 'feedingInstructions')}
        multiline
        multilineRows={2}
        multilineMax={20}
      />
      <InputLabel
        heading="Analytiska beståndsdelar"
        text="Ange den mängd som garanteras av olika näringsämnen, fram till sista förbrukningsdatum (t.ex. av vitaminer). Läs mer om kraven på sidan 123."
        link="https://gs1.se/wp-content/uploads/sites/2/2023/08/handledning-artikelinformation_3.1.24_1_1.pdf"
      />
      <InputTextField
        readOnly={readOnly}
        state={state}
        setState={setState}
        inputName="analyticalConstituentsFeed"
        value={analyticalConstituentsFeed}
        placeholder="Protein: 25, Fett: 15. Lista separerad med komma"
        onChange={(e) => handleAnalyticalConstituentsChange(e.target.value)}
        approve={approve && !!state?.approve?.find(x => x.field === 'analyticalConstituentsFeed')}
        multiline
        multilineRows={2}
        multilineMax={20}
      />
    </div>
  );
}

export default FeedFields;
